import { cloneDeep } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FieldDefinition } from 'sr-types/lib/search/v1/graphql';
import type { EntityName } from '../common/auth/Security';
import SidePanel from '../common/components/layout/SidePanel';
import { SIDE_PANEL_IDS } from '../common/constants/constants';
import I18n from '../common/i18n/I18n';
import { Icons } from '../common/icons/Icons';
import { ScrollableLayout } from '../common/Layout';
import { Attribute } from './attributes';
import AttributesPreview from './AttributesPreview';
import {
    getUpdatedValuesAfterAttributeDeletion,
    getValuesWithUpdatedAssociatedFieldDefinitionReferences,
    getValuesWithUpdatedAttributes
} from './attributesSidePanelUtils';
import { getRawAttributesFromValues } from './attributesUtils';

type AttributeFieldDefinitionReference = {
    id?: string | null;
};

type AttributeValue = {
    fieldDefinitionReference?: AttributeFieldDefinitionReference | null;
};

type AttributeEntityValues = {
    associatedFieldDefinitionReferences?: Array<AttributeFieldDefinitionReference | null> | null;
    attributes?: Array<AttributeValue | null> | null;
    name?: string;
    headline?: string;
};

type AttributeSidePanelType<TValues extends AttributeEntityValues = AttributeEntityValues> = {
    entityId?: string;
    onClose: () => void;
    onSaveEntity?: (values: TValues, isDeletingAttribute: boolean) => Promise<string | undefined>;
    entity: EntityName;
    entityName: string;
    entityValues: TValues | undefined;
    isEntityLoading?: boolean;
};

export default function AttributesSidePanel<TValues extends AttributeEntityValues = AttributeEntityValues>({
    onClose,
    entityId,
    onSaveEntity,
    entity,
    entityName,
    entityValues,
    isEntityLoading
}: AttributeSidePanelType<TValues>) {
    const securedActions = useMemo(
        () => [
            {
                icon: Icons.Close,
                label: <I18n token="dialog.close" />,
                onClick: onClose
            }
        ],
        [onClose]
    );

    const [values, setValues] = useState<TValues>({
        associatedFieldDefinitionReferences: [],
        attributes: [],
        name: '',
        headline: ''
    } as TValues);

    const rawAttributes = useMemo(() => {
        return getRawAttributesFromValues(values.associatedFieldDefinitionReferences, values.attributes);
    }, [values.associatedFieldDefinitionReferences, values.attributes]);

    useEffect(() => {
        if (entityValues) {
            setValues(entityValues);
        }
    }, [entityValues]);

    const handleSetValuesFallback = useCallback((previousValues: TValues) => {
        setValues((currentValues) => ({
            ...currentValues,
            attributes: previousValues.attributes,
            associatedFieldDefinitionReferences: previousValues.associatedFieldDefinitionReferences
        }));
    }, []);

    const handleSaveEntityAfterEditingAttributes = useCallback(
        (previousValues: TValues, updatedValues: TValues, isDeletingAttribute: boolean = false) => {
            if (!onSaveEntity) {
                return;
            }

            onSaveEntity(updatedValues, isDeletingAttribute)
                .then((id) => !id && handleSetValuesFallback(previousValues))
                .catch(() => handleSetValuesFallback(previousValues));
        },
        [handleSetValuesFallback, onSaveEntity]
    );

    const onAttributeAddHandler = useCallback(
        (
            attributeIds: string[],
            getFieldDefinitions: (newFieldDefinition: FieldDefinition) => Attribute[],
            newFieldDefinition: FieldDefinition,
            isTempId: boolean = false
        ) => {
            if (!Array.isArray(attributeIds) || !attributeIds.length) {
                return;
            }
            const previousValues = cloneDeep(values);
            const { updatedValues } = getValuesWithUpdatedAssociatedFieldDefinitionReferences({
                values,
                fieldDefinitions: getFieldDefinitions(newFieldDefinition),
                attributeIds
            });

            setValues(updatedValues);

            if (!isTempId) {
                handleSaveEntityAfterEditingAttributes(previousValues, updatedValues);
            }
        },
        [handleSaveEntityAfterEditingAttributes, values]
    );

    const onEditAttributesSave = useCallback(
        (attributes: Attribute[], handleCloseModal: () => void) => {
            const previousValues = cloneDeep(values);
            const { updatedValues } = getValuesWithUpdatedAttributes({ values, attributes });

            setValues(updatedValues);

            handleSaveEntityAfterEditingAttributes(previousValues, updatedValues);
            handleCloseModal();
        },
        [handleSaveEntityAfterEditingAttributes, values]
    );

    const deleteFieldDefinitionReference = useCallback(
        (fieldDefinitionId: string) => {
            const previousValues = cloneDeep(values);
            const { updatedValues } = getUpdatedValuesAfterAttributeDeletion({ values, fieldDefinitionId });

            setValues(updatedValues);
            handleSaveEntityAfterEditingAttributes(previousValues, updatedValues, true);
        },
        [handleSaveEntityAfterEditingAttributes, values]
    );

    return (
        <ScrollableLayout>
            <SidePanel
                id={SIDE_PANEL_IDS.attributes}
                title={<I18n token="attributes.sidepanel.title" values={{ entityName }} />}
                securedActions={securedActions}
            >
                <AttributesPreview
                    isParentEntityLoading={isEntityLoading}
                    entityId={entityId}
                    rawAttributes={rawAttributes}
                    entity={entity}
                    fetchFromEntity={[entity]}
                    onAttributeAddition={onAttributeAddHandler}
                    onEditAttributesSave={onEditAttributesSave}
                    onChangeAssociatedFieldDefinitionReferences={deleteFieldDefinitionReference}
                    showMessageAfterAttributeAdd={false}
                />
            </SidePanel>
        </ScrollableLayout>
    );
}
